import type { ImportBatch, ImportStatus, Project } from '@servicetim/shared';
import { useImports } from '@servicetim/ui/api';
import { type ColumnDef, DataTable, pagedTableProps } from '@servicetim/ui/components';
import { useMemo } from 'react';
import { formatDateTime, relativeTime } from '../tickets/format';
import { ImportStatusBadge } from './import-status-badge';

const SOURCE_LABEL: Record<ImportBatch['source'], string> = {
  jsm: 'Jira Service Management',
  monday: 'Monday.com',
  csv: 'CSV file',
  fixture: 'Fixture (dev)',
};

export interface ImportsTableProps {
  status?: ImportStatus;
  /** `id -> project` for the "Target" column; imports list has no project name of its own. */
  projectsById: ReadonlyMap<string, Project>;
  onOpen: (batch: ImportBatch) => void;
}

/** `GET /admin/v1/imports` as a keyset-paged table (S-09-01/S-09-02). */
export function ImportsTable({ status, projectsById, onOpen }: ImportsTableProps) {
  const imports = useImports({ status });
  const columns = useMemo<ColumnDef<ImportBatch, unknown>[]>(
    () => [
      {
        id: 'source',
        header: 'Source',
        accessorFn: (b) => b.source,
        cell: ({ row }) => <span className="font-medium">{SOURCE_LABEL[row.original.source]}</span>,
      },
      {
        id: 'project',
        header: 'Target project',
        enableSorting: false,
        cell: ({ row }) => {
          const project = row.original.projectId ? projectsById.get(row.original.projectId) : null;
          return project ? (
            <span>
              {project.name} <span className="text-fg-muted">({project.key})</span>
            </span>
          ) : (
            <span className="text-fg-muted">
              {row.original.createProjects ? 'new projects' : '—'}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (b) => b.status,
        cell: ({ row }) => <ImportStatusBadge status={row.original.status} />,
      },
      {
        id: 'progress',
        header: 'Progress',
        enableSorting: false,
        cell: ({ row }) => {
          const { stats, discovery } = row.original;
          const total = discovery?.ticketCount ?? null;
          if (stats.chunksCompleted === 0) return <span className="text-fg-muted">—</span>;
          return (
            <span className="tabular-nums">
              {stats.recordsSeen}
              {total !== null ? `/${total}` : ''}
              {stats.errors > 0 ? (
                <span className="ml-1 text-red-600 dark:text-red-400 text-xs">
                  ({stats.errors} errors)
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorFn: (b) => b.createdAt,
        cell: ({ row }) => (
          <time dateTime={row.original.createdAt} title={formatDateTime(row.original.createdAt)}>
            {relativeTime(row.original.createdAt)}
          </time>
        ),
      },
    ],
    [projectsById],
  );

  return (
    <DataTable
      columns={columns}
      {...pagedTableProps(imports)}
      getRowId={(b) => b.id}
      onRowClick={onOpen}
      caption="Imports"
      emptyTitle="No imports yet"
      emptyDescription="Bring in tickets from Jira Service Management, Monday.com or a CSV file."
      dense
    />
  );
}
